// frontend/src/services/FileUtils.ts - v2.2
// Changes:
// - Added getDisplayFolderName to centralize folder name cleaning logic
// - Added getFileTypeFromExtension as single source of truth for file-type detection

import { FileType } from './api';

/**
 * Single source of truth for mapping a file key/path to a FileType.
 * Replaces all inline extension switch/if blocks across the frontend.
 * Fixes the mkv routing bug in FolderBrowser (previously missing from its inline switch).
 */
export function getFileTypeFromExtension(key: string): FileType {
  const ext = key.split('.').pop()?.toLowerCase() || '';
  if (['mp4', 'mov', 'webm', 'avi', 'mkv'].includes(ext)) return FileType.VIDEO;
  if (ext === 'csv') return FileType.CSV;
  if (['mp3', 'wav', 'ogg', 'aac'].includes(ext)) return FileType.AUDIO;
  if (['txt', 'log', 'xml', 'opus', 'md'].includes(ext)) return FileType.TEXT;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return FileType.IMAGE;
  if (['pdf', 'doc', 'docx', 'rtf'].includes(ext)) return FileType.DOCUMENT;
  return FileType.OTHER;
}

/**
 * Formats file names for display in the UI, with special handling for ProcessLog files
 * to include their accession ID in the display name, and for video files to remove
 * timestamps and IDs after the first underscore if the name is 20 characters or longer.
 * 
 * @param filePath The full file path/key
 * @returns Formatted file name for display
 */
export const getDisplayFileName = (filePath: string): string => {
  const originalFileName = filePath.split('/').pop() || filePath;

  // Handle ProcessLog CSV files
  if (originalFileName.includes('ProcessLog.csv')) {
    try {
      const pathParts = filePath.split('/');
      if (pathParts.length < 2) {
        console.warn('File path does not contain a parent folder:', filePath);
        return originalFileName;
      }
      const parentFolder = pathParts[pathParts.length - 2];
      const accessionMatch = parentFolder.match(/^(.*?)(?:_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})?$/i);
      if (accessionMatch && accessionMatch[1]) {
        return `${accessionMatch[1]}_ProcessLog.csv`;
      }
    } catch (error) {
      console.error('Error formatting ProcessLog file name:', { filePath, error });
    }
  }

  // Handle video files (mp4, mov, webm)
  const extension = originalFileName.split('.').pop()?.toLowerCase();
  if (['mp4', 'mov', 'webm'].includes(extension || '')) {
    // Split the filename into name and extension
    const parts = originalFileName.split('.');
    const nameWithoutExtension = parts.slice(0, -1).join('.'); // Handle filenames with dots in the name
    const ext = parts[parts.length - 1];

    // Check the length of the name (without extension)
    if (nameWithoutExtension.length >= 20) {
      // Split the name on underscores and take only the first part
      const nameParts = nameWithoutExtension.split('_');
      if (nameParts.length > 1) {
        return `${nameParts[0]}.${ext}`; // Take only the first part and reattach the extension
      }
    }
  }

  return originalFileName;
};

/**
 * Formats folder names for display in the UI by removing file IDs after the last underscore.
 * 
 * @param folderName The folder name to format
 * @returns Formatted folder name for display
 */
export const getDisplayFolderName = (folderName: string): string => {
  if (!folderName) return folderName;
  const parts = folderName.split('_');
  if (parts.length > 1) {
    return parts.slice(0, -1).join('_');
  }
  return folderName;
};

/**
 * Gets the original file name without any special formatting
 * 
 * @param filePath The full file path/key
 * @returns Original file name
 */
export const getOriginalFileName = (filePath: string): string => {
  return filePath.split('/').pop() || filePath;
};